<template>
	<v-card
		:style="{
			borderRadius: standalone ? '0' : '10px',
		}"
	>
		<v-toolbar color="primary" dark flat>
			<v-toolbar-title>
				{{ title }}
			</v-toolbar-title>

			<v-spacer />

			<v-row align="center" justify="end" class="pr-2">
				<template v-if="isAdmin && jsonEditor">
					<v-col cols="auto">
						<v-btn
							variant="elevated"
							color="secondary"
							prepend-icon="mdi-content-save"
							:disabled="!dirty || !isJsonValid"
							@click="updateJson"
						>
							Update JSON

							<v-tooltip location="bottom" activator="parent">
								Note: Only applies, does not save
							</v-tooltip>
						</v-btn>
					</v-col>

					<v-col cols="auto" class="mr-5">
						<v-btn
							variant="elevated"
							color="secondary"
							prepend-icon="mdi-code-json"
							@click="hideRawJsonEditor"
						>
							<v-badge
								color="accent"
								icon="mdi-shield-account"
								offset-x="-7"
								offset-y="-3"
								floating
							>
								Toggle JSON editor
							</v-badge>

							<v-tooltip location="bottom" activator="parent">
								Admin only
							</v-tooltip>
						</v-btn>
					</v-col>
				</template>

				<template v-else>
					<v-col cols="auto">
						<v-btn
							variant="elevated"
							color="secondary"
							prepend-icon="mdi-expand-all"
							:disabled="isAllExpanded"
							@click="expandAllPanels"
						>
							Expand all
						</v-btn>
					</v-col>

					<v-col cols="auto">
						<v-btn
							variant="elevated"
							color="secondary"
							prepend-icon="mdi-collapse-all"
							:disabled="isAllCollapsed"
							@click="collapseAllPanels"
						>
							Collapse all
						</v-btn>
					</v-col>

					<v-col v-if="isAdmin" cols="auto" class="mr-5">
						<v-btn
							variant="elevated"
							color="secondary"
							prepend-icon="mdi-code-json"
							@click="showRawJsonEditor"
						>
							<v-badge
								color="accent"
								icon="mdi-shield-account"
								offset-x="-7"
								offset-y="-3"
								floating
							>
								Toggle JSON editor
							</v-badge>

							<v-tooltip location="bottom" activator="parent">
								Admin only
							</v-tooltip>
						</v-btn>
					</v-col>
				</template>

				<v-divider vertical />

				<v-col v-if="!standalone" cols="auto">
					<v-btn icon="mdi-close" @click="dialog = false"></v-btn>
				</v-col>
				<v-col v-else cols="auto">
					<v-btn icon="mdi-close" @click="closeStandalone"></v-btn>
				</v-col>
			</v-row>
		</v-toolbar>

		<v-col style="overflow-y: auto; flex: 1">
			<v-row v-if="jsonEditor">
				<v-col>
					<v-textarea
						ref="jsonEditor"
						v-model="json"
						bg-color="black"
						class="json-editor"
						validate-on="eager input"
						:rules="[validateJson]"
						:style="{ height: '90vh' }"
					/>
				</v-col>
			</v-row>

			<template v-else>
				<v-row>
					<v-col>
						<v-expansion-panels v-model="panels" multiple>
							<v-expansion-panel
								v-for="{ tagGroupName, headers, items, sortBy } in tableData"
								:key="tagGroupName"
								:value="tagGroupName"
								color="grey-lighten-4"
								static
								@group:selected="
									(...args) => panelSelected(tagGroupName, ...args)
								"
							>
								<template #title>
									<h4 class="text-primary mr-10">
										{{ tagGroupName }}
									</h4>

									<v-tooltip
										v-if="items.length >= itemThreshold"
										location="bottom start"
										activator="parent"
									>
										Closes all other tables when displayed
									</v-tooltip>
								</template>

								<template #text>
									<v-row v-if="panels.includes(tagGroupName)" class="ma-0">
										<v-col cols="12" md="4" class="py-2">
											<v-text-field
												v-model="tableSearches[tagGroupName]"
												label="Search this table"
												prepend-inner-icon="mdi-magnify"
												variant="outlined"
												density="compact"
												hide-details
												clearable
											></v-text-field>
										</v-col>
										<v-col cols="12" md="5" class="py-2">
											<v-select
												v-model="tableColumnFilters[tagGroupName]"
												:items="
													headers.filter(
														(h) =>
															h.key !== 'actions' &&
															h.key !== 'tagGroupInstanceUuid',
													)
												"
												item-title="title"
												item-value="key"
												label="Show only rows with values in..."
												prepend-inner-icon="mdi-filter"
												variant="outlined"
												density="compact"
												multiple
												chips
												closable-chips
												hide-details
												clearable
											></v-select>
										</v-col>
									</v-row>

									<v-data-table
										v-if="
											panels.includes(tagGroupName) &&
											items.length > itemThreshold
										"
										:headers="headers"
										:items="getFilteredItems(items, tagGroupName)"
										:search="tableSearches[tagGroupName]"
										:sort-by="sortBy"
										:items-per-page="50"
										:items-per-page-options="[50, 100]"
										item-key="_id"
										class="raw-data-table"
										fixed-header
										hover
									>
										<template
											v-if="!isAdmin"
											#header.tagGroupInstanceUuid=""
										></template>

										<template
											v-for="(header, indexInner) in headers.filter(
												(h) => h.includeInnerText,
											)"
											#[`item.${header.value}`]="{ item }"
											:key="indexInner"
										>
											<span v-if="!item._isStudyBranchRow">
												{{ item[header.value] }}
											</span>
										</template>

										<template
											v-if="!isAdmin"
											#item.tagGroupInstanceUuid=""
										></template>

										<template #item.actions="{ item }">
											<v-btn
												v-if="!item._isStudyBranchRow && !standalone"
												icon
												variant="text"
												size="small"
												color="secondary"
												@click="loadTagGroup(item.tagGroupInstanceUuid)"
											>
												<v-icon> mdi-target-variant </v-icon>

												<v-tooltip activator="parent">
													Go to tag group
												</v-tooltip>
											</v-btn>
										</template>
									</v-data-table>
									<v-data-table
										v-if="
											panels.includes(tagGroupName) &&
											items.length <= itemThreshold
										"
										:headers="headers"
										:items="getFilteredItems(items, tagGroupName)"
										:search="tableSearches[tagGroupName]"
										:sort-by="sortBy"
										:items-per-page="-1"
										item-key="_id"
										class="raw-data-table"
										fixed-header
										hover
										hide-default-footer
									>
										<template
											v-if="!isAdmin"
											#header.tagGroupInstanceUuid=""
										></template>

										<template
											v-for="(header, indexInner) in headers.filter(
												(h) => h.includeInnerText,
											)"
											#[`item.${header.value}`]="{ item }"
											:key="indexInner"
										>
											<span v-if="!item._isStudyBranchRow">
												{{ item[header.value] }}
											</span>
										</template>

										<template
											v-if="!isAdmin"
											#item.tagGroupInstanceUuid=""
										></template>

										<template #item.actions="{ item }">
											<v-btn
												v-if="!item._isStudyBranchRow && !standalone"
												icon
												variant="text"
												size="small"
												color="secondary"
												@click="loadTagGroup(item.tagGroupInstanceUuid)"
											>
												<v-icon> mdi-target-variant </v-icon>

												<v-tooltip activator="parent">
													Go to tag group
												</v-tooltip>
											</v-btn>
										</template>
									</v-data-table>
								</template>
							</v-expansion-panel>
						</v-expansion-panels>
					</v-col>
				</v-row>
			</template>
		</v-col>
	</v-card>
</template>

<script>
import utils from "@/utils";
import { useMainStore } from "@/store";
import { useLDFlag } from "launchdarkly-vue-client-sdk";
import createCompositeKeyUtils from "@/compositeKeyUtils";
import { backfillTableTagGroups } from "@/pages/RawDataViewer/backfillTableTagGroups";

// ordering for definitions and abbreviations
const definitionsAndAbbreviationsOrder = ["Term", "Description", "Comments"];

export default {
	name: "VRawDataViewerData",

	props: {
		modelValue: {
			type: Boolean,
			required: true,
			default: false,
		},
		rawData: {
			type: Object,
			required: true,
			default: () => ({}),
		},
		fileNames: {
			type: Array,
			required: true,
			default: () => [],
		},
		moduleWindow: {
			type: Object,
			required: false,
			default: null,
		},
		standalone: {
			type: Boolean,
			required: false,
			default: false,
		},
	},

	emits: ["update:modelValue", "updateJson", "reload-data"],

	setup() {
		const collapseTuplesIntoSingleTgi = useLDFlag(
			"collapse-tuples-into-single-tgi",
		);
		return {
			collapseTuplesIntoSingleTgi,
			itemThreshold: 100,
			title: "Raw Data Viewer",
		};
	},

	data() {
		return {
			compositeKeyUtils: null,
			dirty: false,
			isAdmin: false,
			json: "",
			jsonEditor: false,
			panels: [],
			tableSearches: {},
			tableColumnFilters: {},
			localRawData: null,
		};
	},

	computed: {
		dialog: {
			get() {
				return this.modelValue;
			},
			set(newValue) {
				this.$emit("update:modelValue", newValue);
			},
		},
		isAllCollapsed() {
			return this.panels.length === 0;
		},
		isAllExpanded() {
			return (
				this.panels.length ===
				this.tableData.filter(
					(table) => table.items.length <= this.itemThreshold,
				).length
			);
		},
		tableData() {
			const data = this.transformDataToTables(this.rawData);
			return data;
		},
		user() {
			return useMainStore().user;
		},
		isJsonValid() {
			try {
				JSON.parse(this.json);
				return true;
			} catch (error) {
				return false;
			}
		},
	},

	watch: {
		rawData: {
			deep: true,
			immediate: true,
			handler(newVal) {
				this.json = JSON.stringify(newVal, null, "\t");
				this.localRawData = JSON.parse(JSON.stringify(newVal));
				this.dirty = false;
			},
		},
		dialog(newValue) {
			if (!newValue && !this.standalone) {
				this.clearAll();
			}
		},
	},

	async mounted() {
		this.isAdmin = useMainStore().isAdmin;
		this.expandAllPanels();

		// Listen for messages from parent window
		window.addEventListener("message", this.handleParentMessage);
	},

	beforeUnmount() {
		window.removeEventListener("message", this.handleParentMessage);
	},

	methods: {
		getFilteredItems(items, tagGroupName) {
			// Filter items based on selected column filters
			const selectedColumns = this.tableColumnFilters[tagGroupName];

			if (!selectedColumns || selectedColumns.length === 0) {
				return items;
			}

			// Only show rows where ALL selected columns have non-null values
			return items.filter((item) => {
				return selectedColumns.every((columnKey) => {
					const value = item[columnKey];
					return value != null && value !== "";
				});
			});
		},
		clearAll() {
			this.dirty = false;
			this.isAdmin = false;
			this.json = "";
			this.jsonEditor = false;
			this.clearSearch();
			this.clearPanels();
		},
		clearSearch() {
			this.tableSearches = {};
			this.tableColumnFilters = {};
		},
		clearPanels() {
			this.panels = [];
		},
		expandAllPanels() {
			this.panels = this.tableData
				// do not expand tables with more than threshold items
				.filter((table) => table.items.length <= this.itemThreshold)
				.map((table) => table.tagGroupName);
		},
		collapseAllPanels() {
			this.clearPanels();
		},
		panelSelected(tagGroupName, { value: expanded }) {
			const table = this.tableData.find(
				(table) => table.tagGroupName === tagGroupName,
			);
			if (expanded && table.items.length > this.itemThreshold) {
				// if a "large" panel is selected, close all other panels
				this.collapseAllPanels();
				this.panels.push(tagGroupName);
			}
		},
		showRawJsonEditor() {
			this.jsonEditor = true;
		},
		hideRawJsonEditor() {
			this.jsonEditor = false;
		},
		updateJson() {
			// If in standalone mode, send message to parent window
			if (this.standalone) {
				window.opener?.postMessage(
					{
						type: "UPDATE_JSON_FROM_VIEWER",
						json: this.json,
					},
					window.location.origin,
				);
				// Update the local copy to match the new JSON so dirty flag resets
				this.localRawData = JSON.parse(this.json);
				this.dirty = false;
				this.jsonEditor = false;
			} else {
				this.$emit("updateJson", this.json);
				this.dialog = false;
			}
		},
		validateJson() {
			try {
				JSON.parse(this.json);
				this.checkDirty();
				return true;
			} catch (error) {
				return error.message;
			}
		},
		checkDirty: function () {
			if (
				JSON.stringify(JSON.parse(this.json)) ===
				JSON.stringify(this.localRawData)
			) {
				this.dirty = false;
			} else {
				this.dirty = true;
			}
		},
		identifyAxis(axis) {
			if (!axis.metadata) {
				return "text";
			} else if (axis.metadata.table) {
				return "table";
			} else {
				if (axis.metadata.barHeaders) {
					return "bar";
				} else {
					return "xy";
				}
			}
		},
		transformDataToTables(rawData) {
			const { tags } = rawData;
			// Legacy table saves have no per-entry tagGroup; without this every
			// such table is filtered out below (see backfillTableTagGroups.js).
			const { axesColl, datasetColl } = backfillTableTagGroups(
				rawData.axesColl,
				rawData.datasetColl,
			);

			if (tags) {
				this.compositeKeyUtils = createCompositeKeyUtils(tags);
				this.studyBranchOrder = this.compositeKeyUtils.branchTags;
			}

			// short-circuit if datasetColl is empty
			if (!Array.isArray(datasetColl) || datasetColl.length === 0) {
				return [];
			}

			const tablesMap = this.buildTablesMap(axesColl, datasetColl, tags);
			return this.convertMapToTables(tablesMap);
		},
		buildPointGroupMap(axis, tags) {
			const pointGroupMap = {};

			const axisType = this.identifyAxis(axis);
			if (["xy", "bar"].includes(axisType) && axis.metadata?.pointGroups) {
				const axesLabelData = axis.metadata.axesLabelData;
				const pointGroupSettings = axis.metadata.pointGroups;
				const pointGroupAxes = Object.keys(pointGroupSettings);

				const findTagUuid = (tagName) =>
					tags.find((tag) => tag.name === tagName)?.uuid;

				pointGroupAxes.forEach((axisDirection) => {
					const pointGroups = pointGroupSettings[axisDirection];

					// build point group map
					pointGroups.forEach((pointGroup) => {
						if (!pointGroupMap[axisDirection]) {
							pointGroupMap[axisDirection] = {};
						}

						// get main data type for the axis
						let mainDataType;
						if (axisType === "bar") {
							mainDataType = axesLabelData.bar.dataType;
						} else {
							mainDataType = axesLabelData[axisDirection].dataType;
						}

						if (!pointGroupMap[axisDirection][findTagUuid(mainDataType)]) {
							pointGroupMap[axisDirection][findTagUuid(mainDataType)] = [];
						}
						pointGroupMap[axisDirection][findTagUuid(mainDataType)].push(
							pointGroup.uuid,
						);
					});
				});
			}

			return pointGroupMap;
		},
		buildTablesMap(axesColl, datasetColl, tags) {
			const tablesMap = new Map();

			datasetColl.forEach((dataset) => {
				const page = dataset.page;
				const file = this.fileNames[dataset.file];
				const axis = axesColl.find((axis) => axis.name === dataset.axesName);

				// if the dataset has point groups, collapse tuples into single tag group instances
				let pointGroupMap = {};
				if (this.collapseTuplesIntoSingleTgi) {
					pointGroupMap = this.buildPointGroupMap(axis, tags);
				}

				dataset.data.forEach((dataPoint) => {
					this.processDataPoint(
						axis,
						dataset,
						dataPoint,
						file,
						page,
						tablesMap,
						pointGroupMap,
					);
				});
			});

			return tablesMap;
		},
		processDataPoint(
			axis,
			dataset,
			dataPoint,
			file,
			page,
			tablesMap,
			pointGroupMap,
		) {
			const { metadata } = dataPoint;
			if (!metadata) return;

			const tagGroupInfo = this.extractTagGroupInfo(metadata);
			if (!tagGroupInfo) return;

			const tagValues = this.extractTagValues(
				metadata,
				tagGroupInfo,
				axis,
				dataset,
				pointGroupMap,
			);

			// if tagValues is null, skip adding to the table
			// this skips the data points that have been collapsed into the main
			// data point of tuples
			if (!this.collapseTuplesIntoSingleTgi || tagValues) {
				this.addToTablesMap(tagGroupInfo, tagValues, file, page, tablesMap);
			}
		},
		extractTagGroupInfo(metadata) {
			const tagGroup = metadata.tagGroup;
			const tagGroups = metadata.tagGroups;
			if (tagGroups) {
				const filtered = tagGroups.filter((tg) => tg.uuid && tg.instanceUuid);
				if (filtered.length === 0) {
					return null;
				}

				return filtered;
			} else {
				if (!tagGroup?.uuid || !tagGroup?.instanceUuid) {
					return null;
				}

				return [
					{
						name: tagGroup.name,
						uuid: tagGroup.uuid,
						instanceUuid: tagGroup.instanceUuid,
					},
				];
			}
		},
		findPointGroupData(metadata, axisType, axis, pointGroupUuid, value) {
			for (const [key, data] of Object.entries(metadata)) {
				if (
					utils.isNumber(key) &&
					data?.axis === axis &&
					(!pointGroupUuid || data?.tag?.uuid === pointGroupUuid) &&
					(axisType === "bar" || data?.axisType === "value") &&
					(value === undefined || data?.value === value)
				) {
					return data;
				}
			}
			return null;
		},
		// Like findPointGroupData, but returns EVERY matching entry on the axis
		// (the full set of identity tags), not just the first. Used to match a
		// point-group bound to its center on the complete shared-axis identity.
		findAllPointGroupData(metadata, axisType, axis) {
			const out = [];
			for (const [key, data] of Object.entries(metadata)) {
				if (
					utils.isNumber(key) &&
					data?.axis === axis &&
					(axisType === "bar" || data?.axisType === "value") &&
					data?.tag?.uuid
				) {
					out.push(data);
				}
			}
			return out;
		},
		getTupleData(mainMetadata, axisType, dataset, pointGroupMap) {
			let key = -1;
			const tupleData = {};

			// iterate over each axis and point group map
			for (const [axis, pointGroupTagMap] of Object.entries(pointGroupMap)) {
				// find point group data in given metadata, optionally filter by point group uuid and value
				// get the main point group data
				const mainPointGroupUuid = Object.keys(pointGroupTagMap)[0];
				const mainPointGroupData = this.findPointGroupData(
					mainMetadata,
					axisType,
					axis,
					mainPointGroupUuid,
				);

				// if the main point group data is not found, skip this point group axis
				if (!mainPointGroupData) {
					continue;
				} else {
					// Match each point-group bound to its center using the WPD
					// tuple id (metadata.tuple[axis].id) -- the canonical logical
					// linkage where the same id means the same tuple and a bound
					// carries its center's id. The earlier shared-axis VALUE match
					// collapsed bars that share an x tag value (e.g. every bar
					// labelled Count=123, differing only by their mean) onto a
					// single center. Fall back to the full shared-axis identity for
					// legacy data that predates tuple ids.
					const sharedAxis = axis === "x" ? "y" : "x";
					const centerTupleId = mainMetadata.tuple?.[axis]?.id ?? null;
					const sharedIdentity = this.findAllPointGroupData(
						mainMetadata,
						axisType,
						sharedAxis,
					);

					// go through each data point in the dataset
					for (const dataPoint of dataset.data) {
						// The data point belongs to this center's tuple if their
						// tuple ids match; without tuple ids, fall back to sharing
						// every shared-axis value tag.
						const dpTupleId = dataPoint.metadata.tuple?.[axis]?.id ?? null;
						const belongsToTuple =
							centerTupleId && dpTupleId
								? dpTupleId === centerTupleId
								: sharedIdentity.length > 0 &&
								  sharedIdentity.every((shared) =>
										this.findPointGroupData(
											dataPoint.metadata,
											axisType,
											sharedAxis,
											shared.tag.uuid,
											shared.value,
										),
								  );

						if (!belongsToTuple) continue;

						// check whether this data point belongs to any of the
						// point groups registered on this axis
						const matchesPointGroup = pointGroupTagMap[mainPointGroupUuid].some(
							(pointGroupUuid) =>
								this.findPointGroupData(
									dataPoint.metadata,
									axisType,
									axis,
									pointGroupUuid,
								),
						);

						if (!matchesPointGroup) continue;

						// Merge every numeric metadata entry from the matched
						// point-group data point so extra tag-value pairs on it
						// (e.g. "Confidence level = 95") survive the tuple collapse.
						// Entries that duplicate the center (shared-axis value, units,
						// descriptions) hit the (tag, value) dedup in
						// addTagValuePairToGroup and are silently dropped.
						for (const [mKey, mData] of Object.entries(dataPoint.metadata)) {
							if (utils.isNumber(mKey) && mData?.tag) {
								tupleData[`${key--}`] = mData;
							}
						}
					}
				}
			}

			return tupleData;
		},
		extractTagValues(metadata, tagGroupInfo, axis, dataset, pointGroupMap) {
			const tagValues = {};
			const axisType = this.identifyAxis(axis);
			const axisMetadata = axis.metadata;

			// Collapse tuple to a single TGI for graphs when allowed
			let tupleData = {};
			if (
				this.collapseTuplesIntoSingleTgi &&
				["xy", "bar"].includes(axisType) &&
				pointGroupMap
			) {
				const hasNonMainPointGroup = Object.keys(pointGroupMap).some((pgAxis) =>
					Object.values(pointGroupMap[pgAxis])
						.flat()
						.some((uuid) =>
							this.findPointGroupData(metadata, axisType, pgAxis, uuid),
						),
				);

				if (hasNonMainPointGroup) return null;
				tupleData = this.getTupleData(
					metadata,
					axisType,
					dataset,
					pointGroupMap,
				);
			}

			const hasNonHeaderDataMap = new Set();

			// helper to figure out which header a piece of data came from
			const findHeaderType = (tagName, value) => {
				if (!(axisType === "table" && axisMetadata.calibration)) return null;
				for (const type of ["rows", "cols"]) {
					const headers = axisMetadata.calibration[type]?.headers;
					if (!headers) continue;
					const found = headers.some(
						(header) =>
							header &&
							Object.keys(header)
								.filter((k) => !isNaN(k))
								.some(
									(k) =>
										header[k]?.tag?.name === tagName &&
										header[k]?.value === value,
								),
					);
					if (found) return type;
				}
				return null;
			};

			// Walks the `tagName`, `tagName (1)`, ... slots for this scope (the
			// scope is keyed by `suffix`, e.g. `[X]`, `[Column header]`, or none).
			// Appends at the first empty slot, but no-ops if the same value is
			// already present. The value-dedup is load-bearing: the tuple-collapse
			// merge above feeds the same (tag, value) multiple times by design.
			const addTagValuePairToGroup = (groupObj, tagName, value, suffix) => {
				const hasSuffix = !!suffix;
				const keyFor = (n) => {
					const numbered = n === 0 ? "" : ` (${n})`;
					return hasSuffix
						? `${tagName}${numbered} ${suffix}`
						: `${tagName}${numbered}`;
				};
				for (let n = 0; ; n++) {
					const k = keyFor(n);
					if (!groupObj[k]) {
						groupObj[k] = value;
						return;
					}
					if (groupObj[k] === value) return;
				}
			};

			const headerTypeDisplay = {
				rows: "Row header",
				cols: "Column header",
			};

			const tableCellHasNonHeaderData = (items, tagGroupUuid) => {
				return items
					.filter(([, data]) => data.tagGroup?.uuid === tagGroupUuid)
					.some(([, data]) => !data.headerTag && data.value !== null);
			};

			// add extra study branches for multiple tag groups so the display
			// shows the correct study branch data
			let extraStudyBranchData = [];
			if (tagGroupInfo.length > 1) {
				Object.entries(metadata).forEach(([key, data]) => {
					if (utils.isNumber(key) && data?.tagGroup && data?.tag) {
						// find study branch data
						if (this.compositeKeyUtils.isBranchTagGroup(data.tag.name)) {
							// add extra study branch data for all other tag groups
							for (const tagGroup of tagGroupInfo.filter(
								(tagGroup) => tagGroup.uuid !== data.tagGroup.uuid,
							)) {
								extraStudyBranchData.push([
									key,
									Object.assign({}, data, {
										tagGroup: tagGroup,
									}),
								]);
							}
						}
					}
				});
			}

			const items = [
				...Object.entries(metadata),
				...Object.entries(tupleData),
				...extraStudyBranchData,
			].filter(([key, data]) => utils.isNumber(key) && data?.tag);

			items.forEach(([, data]) => {
				const tagGroupUuid = data.tagGroup
					? data.tagGroup.uuid
					: metadata.tagGroup.uuid;

				// skip if table cell only contains header data
				if (
					axisType === "table" &&
					!tableCellHasNonHeaderData(items, tagGroupUuid)
				) {
					return;
				}

				if (!tagValues[tagGroupUuid]) tagValues[tagGroupUuid] = {};
				const group = tagValues[tagGroupUuid];

				if (
					!group["Container"] &&
					!this.studyBranchOrder.includes(data.tag.name)
				) {
					group["Container"] = axis.name;
				}
				if (axisType === "xy" && data.axis && axisMetadata.axesLabelData) {
					// `[X]` / `[Y]` is enough to pin a tag to its axis. The axis's
					// central-tendency name adds no information (it's constant for
					// every tag on the axis) and misreads on point-group columns.
					const suffix = `[${data.axis.toUpperCase()}]`;
					addTagValuePairToGroup(
						group,
						data.tag.name,
						data.value ?? null,
						suffix,
					);
				} else if (axisType === "table" && axisMetadata.calibration) {
					if (data.headerTag) {
						const headerType = findHeaderType(data.tag.name, data.value);

						addTagValuePairToGroup(
							group,
							data.tag.name,
							data.value ?? null,
							`[${headerTypeDisplay[headerType]}]`,
						);
					} else {
						addTagValuePairToGroup(group, data.tag.name, data.value ?? null);
					}

					if (this.studyBranchOrder.includes(data.tag.name)) {
						group[data.tag.name] = data.value ?? null;
					}
				} else {
					addTagValuePairToGroup(group, data.tag.name, data.value ?? null);
				}

				hasNonHeaderDataMap.add(tagGroupUuid);
			});

			Object.keys(tagValues).forEach((uuid) => {
				if (!hasNonHeaderDataMap.has(uuid)) delete tagValues[uuid];
			});

			return tagValues;
		},
		addToTablesMap(tagGroups, tagValues, file, page, tablesMap) {
			tagGroups.forEach((tagGroup) => {
				const { uuid: tagGroupUuid, instanceUuid } = tagGroup;

				if (!tablesMap.has(tagGroupUuid)) {
					tablesMap.set(tagGroupUuid, {
						tagGroupName: tagGroup.name,
						tagGroupUuid,
						rows: new Map(),
					});
				}

				const tableData = tablesMap.get(tagGroupUuid);

				// Get or create row
				if (!tableData.rows.has(instanceUuid)) {
					tableData.rows.set(instanceUuid, {});
				}

				const row = tableData.rows.get(instanceUuid);

				const values = tagValues[tagGroupUuid];
				if (!values) {
					return;
				}

				const tags = Object.keys(values);
				const isStudyBranch =
					tags.length === 1 && this.studyBranchOrder.includes(tags[0]);

				// Merge tag values and metadata using spread operator
				Object.assign(row, {
					...values,
					tagGroupInstanceUuid: instanceUuid,
					File: isStudyBranch ? null : file,
					Page: isStudyBranch ? null : page,
				});
			});
		},
		convertMapToTables(tablesMap) {
			const tables = [];

			for (const [tagGroupUuid, tableData] of tablesMap.entries()) {
				const { tagGroupName, rows } = tableData;

				const headers = this.buildHeaders(rows, tagGroupName);
				const items = this.buildItems(rows, headers, tagGroupName);
				const sortBy = this.buildSortBy(headers, tagGroupName);

				tables.push({
					tagGroupName,
					tagGroupUuid,
					headers,
					items,
					sortBy,
				});
			}

			return tables;
		},
		buildHeaders(rows, tagGroupName) {
			// Gather all distinct header names
			const allHeaderNames = new Set();
			for (const row of rows.values()) {
				Object.keys(row).forEach((headerName) => {
					allHeaderNames.add(headerName);
				});
			}

			const headers = Array.from(allHeaderNames);
			return this.orderHeaders(headers, tagGroupName);
		},
		orderHeaders(headers, tagGroupName) {
			const locationHeaders = ["Container", "File", "Page"];

			// Enforce study-branch ordering at the front
			const orderedStudyBranches = this.studyBranchOrder.filter((header) =>
				headers.includes(header),
			);

			let otherHeaders;

			if (tagGroupName === "Definitions and abbreviations") {
				otherHeaders = definitionsAndAbbreviationsOrder.filter((header) =>
					headers.includes(header),
				);
			} else {
				otherHeaders = headers
					.filter(
						(header) =>
							!this.studyBranchOrder.includes(header) &&
							!locationHeaders.includes(header),
					)
					.sort();
			}

			const mainHeaders = [...orderedStudyBranches, ...otherHeaders].map(
				(header) => ({
					title: header,
					key: header,
					width: "200px",
				}),
			);

			const locationHeaderConfigs = locationHeaders.map((header) => ({
				title: header,
				key: header,
				width: "200px",
				...(header === "File" ? {} : { align: "center", width: "150px" }),
				...(header === "Container" ? { minWidth: "500px" } : {}),
			}));

			const actionHeader = {
				title: "Actions",
				key: "actions",
				align: "center",
				width: "75px",
				sortable: false,
			};

			return [...mainHeaders, ...locationHeaderConfigs, actionHeader];
		},
		buildItems(rows, headers, tagGroupName) {
			const items = [];
			const studyBranchRows = [];
			for (const row of rows.values()) {
				if (this.hasNonEmptyValues(row)) {
					const item = {};
					headers.forEach((header) => {
						item[header.key] = row[header.key] ?? null;
					});

					// Use tagGroupInstanceUuid as stable unique identifier
					item._id = item.tagGroupInstanceUuid;

					// Pre-compute if this is a study branch row for template
					item._isStudyBranchRow = !item.Page && !item.File;
					items.push(item);
				} else if (this.isEmptyStudyBranchRow(row)) {
					const item = {};
					headers.forEach((header) => {
						item[header.key] = row[header.key] ?? null;
					});

					// Use tagGroupInstanceUuid as stable unique identifier
					item._id = item.tagGroupInstanceUuid;

					// Pre-compute if this is a study branch row for template
					item._isStudyBranchRow = !item.Page && !item.File;
					studyBranchRows.push(item);
				}
			}

			if (this.compositeKeyUtils) {
				const itemCompositeKeys = new Set();

				for (const item of items) {
					const compositeKey = this.compositeKeyUtils.generateCompositeKey(
						...this.studyBranchOrder.map((branch) => item[branch]),
					);
					itemCompositeKeys.add(compositeKey);
				}

				for (const row of studyBranchRows) {
					const studyBranchCompositeKey =
						this.compositeKeyUtils.generateCompositeKey(
							...this.studyBranchOrder.map((branch) => row[branch]),
						);

					if (
						!itemCompositeKeys.has(studyBranchCompositeKey) &&
						this.compositeKeyUtils.isBranchTagGroup(tagGroupName)
					) {
						items.push(row);
						itemCompositeKeys.add(studyBranchCompositeKey);
					}
				}
			} else {
				console.error("Composite key utils not found");
				items.push(...studyBranchRows);
			}

			return items;
		},
		buildSortBy(headers, tagGroupName) {
			if (tagGroupName === "Definitions and abbreviations") {
				return [
					{
						key: "Term",
						order: "asc",
					},
				];
			} else {
				return this.studyBranchOrder
					.filter((header) => headers.some((h) => h.key === header))
					.map((header) => ({
						key: header,
						order: "asc",
					}));
			}
		},
		hasNonEmptyValues(row) {
			const locationHeaders = ["File", "Page"];

			return Object.entries(row).some(([header, val]) => {
				if (val == null) return false;

				return (
					!this.studyBranchOrder.includes(header) &&
					!locationHeaders.includes(header) &&
					header !== "tagGroupInstanceUuid"
				);
			});
		},
		isEmptyStudyBranchRow(row) {
			return (
				!this.hasNonEmptyValues(row) &&
				Object.entries(row).some(([header, val]) => {
					if (val == null) return false;

					return this.studyBranchOrder.includes(header);
				})
			);
		},
		loadTagGroup(tagGroupInstanceUuid) {
			this.dialog = false;

			this.moduleWindow.document.dispatchEvent(
				new CustomEvent("select-data-point", {
					detail: {
						tagGroupInstanceUuid,
					},
				}),
			);
		},
		isStudyBranchRow(item) {
			return !item.page && !item.file;
		},
		handleParentMessage(event) {
			// Only accept messages from our own domain
			if (event.origin !== window.location.origin) return;

			if (event.data.type === "SAVE_COMPLETED") {
				// reload SDE data from the server
				this.$emit("reload-data");
				this.dirty = false;
				this.expandAllPanels();
			}
		},
		closeStandalone() {
			// Close the window
			window.close();
		},
	},
};
</script>

<style lang="scss">
.raw-data-viewer,
.raw-data-viewer-standalone {
	.raw-data-table {
		th {
			font-weight: bold !important;
		}

		.v-table__wrapper {
			max-height: 500px;
		}

		// Allow text to wrap
		td,
		th {
			word-wrap: break-word;
			overflow-wrap: break-word;
		}
	}

	.v-textarea.json-editor textarea {
		@extend .fira-code-regular;
		height: 80vh;
	}
}
</style>
